import { gql, useQuery } from '@apollo/client'
import { useMemo } from 'react'

import { useDateFilter } from '../../../dateFilter'
import { useGroupFilter } from '../../../groupFilter'
import calc from '../../../utils/calc'
import { IApiDataType } from '../../types'
import buildMetricValuesHook from '../utils/buildMetricValuesHook'

export const dhWbListSystemwideOverviewKpisConfigs = {
  netSales: 'price',
  checkCount: 'number',
  checkAverage: 'price',
  netSalesSssPercent: 'percent',
  laborTotalCostPercent: 'percent',
  averageWeeklySales: 'price',
  cogsPercentage: 'percent',
  expoMSssPercentage: 'percent',
} as const

type IMetricDataType = Record<
  string,
  {
    name: string
    unit: 'CENT' | 'PERCENTAGE' | 'DOLLAR' | 'COUNT'
    value: number
  }
>

type IDataType<T extends string = 'listLocationGroupAverageWeeklySales'> =
  Record<
    T,
    {
      nodes: Record<'metricSummaryData', IMetricDataType>[]
    }
  >

const query = gql`
  query ListLocationGroupAverageWeeklySales(
    $iStartDate: Date!
    $iEndDate: Date!
    $iFilter: JSON!
  ) {
    listLocationGroupAverageWeeklySales(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iFilter: $iFilter
    ) {
      nodes {
        metricSummaryData
      }
    }
  }
`

const useCommonDhWbSystemwideOverview = buildMetricValuesHook(
  {
    groupFilterType: 'corporate',
    metrics: {
      codes: [
        'net_sales',
        { key: 'net_sales', type: 'yoy' },
        'check_count',
        'check_average',
        'labor_total_cost_percent',
        'cogs_percentage',
        'expo_m_sss_percentage',
      ],
    },
  },
  (data) => ({
    ...data,

    // This is actually YoY%, not SSS%
    netSalesSssPercent:
      calc(
        calc(calc(data.netSales, '-', data.yoyNetSales), '/', data.yoyNetSales),
        '*',
        100,
      ) || null,
  }),
)

const useDhWbListSystemwideOverviewKpis = () => {
  const { data: commonData, loading: commonLoading } =
    useCommonDhWbSystemwideOverview()
  const { startDate, endDate } = useDateFilter()
  const { groupFilter } = useGroupFilter()
  const { data, loading } = useQuery<IDataType>(query, {
    variables: {
      iStartDate: startDate,
      iEndDate: endDate,
      iFilter: {
        location_group_ids: groupFilter?.ids,
        metrics: ['net_sales'],
      },
    },
    skip: !startDate || !endDate || !groupFilter,
  })

  return {
    data: useMemo((): IApiDataType => {
      if (!commonData) return commonData

      const nodes = data?.listLocationGroupAverageWeeklySales?.nodes
      const averageWeeklySales = nodes?.find((n) => n.metricSummaryData)
        ?.metricSummaryData?.averageWeeklySales?.value

      return {
        ...commonData,
        averageWeeklySales: averageWeeklySales
          ? averageWeeklySales * 100
          : null,
      }
    }, [commonData, data]),
    loading: commonLoading || loading,
  }
}

export default useDhWbListSystemwideOverviewKpis
